import styled from 'styled-components'

export interface DonutSegment {
    key: string
    label: string
    value: number
    color: string
}

interface DonutChartProps {
    segments: DonutSegment[]
    size?: number
    thickness?: number
    onSegmentClick?: (segment: DonutSegment) => void
}

const GAP = 2

// Multiplies each RGB channel by `factor` (<1 darkens) to get a hover shade.
// Used instead of a CSS `filter` because filters render unreliably on SVG strokes.
const darken = (hex: string, factor = 0.78): string => {
    const normalized = hex.replace('#', '')
    const channel = (start: number) => Math.round(parseInt(normalized.slice(start, start + 2), 16) * factor)
    const toHex = (value: number) => Math.min(value, 255).toString(16).padStart(2, '0')
    return `#${toHex(channel(0))}${toHex(channel(2))}${toHex(channel(4))}`
}

const DonutChart = ({ segments, size = 150, thickness = 28, onSegmentClick }: DonutChartProps) => {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0)
    const radius = (size - thickness) / 2
    const circumference = 2 * Math.PI * radius
    const center = size / 2

    let cumulativeFraction = 0

    return (
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
            <g transform={`rotate(-90 ${center} ${center})`}>
                {total === 0 ? (
                    <circle cx={center} cy={center} r={radius} fill="none" stroke="#e0e0e0" strokeWidth={thickness} />
                ) : (
                    segments
                        .filter((segment) => segment.value > 0)
                        .map((segment) => {
                            const fraction = segment.value / total
                            const dash = Math.max(fraction * circumference - GAP, 0)
                            const offset = -cumulativeFraction * circumference
                            cumulativeFraction += fraction

                            return (
                                <Segment
                                    key={segment.key}
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="none"
                                    stroke={segment.color}
                                    strokeWidth={thickness}
                                    strokeDasharray={`${dash} ${circumference - dash}`}
                                    strokeDashoffset={offset}
                                    $clickable={!!onSegmentClick}
                                    $hoverColor={darken(segment.color)}
                                    onClick={() => onSegmentClick?.(segment)}
                                >
                                    <title>{`${segment.label}: ${segment.value}`}</title>
                                </Segment>
                            )
                        })
                )}
            </g>
        </Svg>
    )
}

const Svg = styled.svg`
    display: block;
`

const Segment = styled.circle<{ $clickable: boolean; $hoverColor: string }>`
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    transition: stroke 0.15s ease;

    &:hover {
        stroke: ${({ $clickable, $hoverColor }) => ($clickable ? $hoverColor : undefined)};
    }
`

export default DonutChart
